import {
  Callout,
  DocPage,
  DocSection,
  K,
  MCP_URL,
  P,
  PageFooter,
  PromptCard,
  UL,
  UrlBlock,
} from "./_components";

export function Endpoints() {
  return (
    <DocPage
      eyebrow="Concepts"
      title="Workshop vs project endpoints"
      intro={
        <>
          MCPBuild gives you <em>two</em> MCP URLs, and they do completely
          different jobs. Knowing which to connect (and when) is the single
          biggest unlock — it's the difference between "I can build anywhere"
          and "I can hand a demo to my CEO without explaining anything."
        </>
      }
    >
      <DocSection id="workshop" title="The workshop endpoint — for builders">
        <UrlBlock label="Workshop MCP URL" url={MCP_URL} />
        <P>
          Connect this in your day-to-day Claude or ChatGPT. The workshop
          exposes <strong>builder tools</strong> only — the tools you use to
          create, edit, and organize projects:
        </P>
        <UL>
          <li>
            <K>dynamic_widget</K> — write a React widget from a description.
          </li>
          <li>
            <K>project</K> — umbrella for create / list / publish / share /
            remix / scaffold / set_trigger_phrases / add_data_tool, etc.
          </li>
          <li>
            <K>remix_widget</K> — re-write a widget's code (e.g. "make it dark
            mode", "add a column").
          </li>
          <li>
            <K>widget</K> — get / list versions / restore / delete on a single
            widget.
          </li>
          <li>
            <K>validate_widget</K> — dry-compile React without saving, so the AI
            can self-correct before persisting.
          </li>
        </UL>
        <Callout kind="note" title="The workshop never CALLS your project's tools.">
          If you ask "cancel order A-100" while connected to the workshop, the
          AI has no idea what that means. To <em>use</em> the tools you built,
          connect the project endpoint instead.
        </Callout>
      </DocSection>

      <DocSection id="project" title="The project endpoint — for callers (and demos)">
        <UrlBlock label="Project MCP URL pattern" url={`${MCP_URL}/<your-project-slug>`} />
        <P>
          Every project gets its own URL — for example,{" "}
          <K>{MCP_URL}/orders</K>. Connect a project endpoint and the AI sees
          only that project's exposed tools (the ones toggled "live"). No
          builder surface, no clutter, no risk of someone accidentally calling{" "}
          <K>project delete</K>.
        </P>
        <P>
          Project endpoints also include one built-in: <K>fetch</K>. Widgets
          inside the project call it via the SDK's <K>fetchData()</K> to talk to
          real external APIs through your domain allowlist + KMS-encrypted
          secrets. See <a href="/docs/live-data">Live data & secrets</a>.
        </P>
        <Callout kind="tip" title="This is what you give to your demo audience.">
          Send the project URL — not the workshop URL — to anyone using your
          tools. They get a focused MCP server with exactly the tools they
          need.
        </Callout>
      </DocSection>

      <DocSection id="which" title="Quick decision: which one do I connect?">
        <P>
          <strong>You, while iterating:</strong> Workshop. Plus the project
          endpoint of whatever you're working on, so you can also try calling
          your tools to test them.
        </P>
        <P>
          <strong>Audience, during a demo:</strong> Just the project endpoint.
        </P>
        <P>
          <strong>Teammate who will edit the project:</strong> Workshop +
          they'll need write access to the project (see{" "}
          <a href="/docs/sharing">Sharing</a>).
        </P>
        <P>
          <strong>End-user who'll just chat with your tool:</strong> Project
          endpoint only.
        </P>
      </DocSection>

      <DocSection id="public" title="Public project endpoints — no login required">
        <P>
          On the project page hit <K>Publish</K>. You get two URLs:
        </P>
        <UL>
          <li>
            <K>mcpbuild.app/p/&lt;token&gt;</K> — a webpage that renders the
            project's widgets. No connector setup; share via Slack, email, post
            it on a slide.
          </li>
          <li>
            <K>{MCP_URL}/public/&lt;token&gt;</K> — a no-auth MCP endpoint
            anyone can connect to in Claude or ChatGPT. Same tools as your
            private project endpoint, minus mutating ones.
          </li>
        </UL>
      </DocSection>

      <DocSection id="example" title="A prompt that uses both endpoints">
        <PromptCard
          title="Workshop: build a tool you'll consume on the project endpoint"
          prompt={`Add a data tool called "city-weather" to my "Weather" project. Description: "Look up a fictional city's current weather. Call when the user asks about a city by name." inputSchema { type:"object", properties:{ city:{type:"string"} }, required:["city"] }, annotations { readOnlyHint: true }, data { city:"Boston", tempF:54, condition:"Cloudy" }, triggerPhrases ["what's the weather in Boston","how cold is it today"].`}
          note={
            <>
              Run this prompt while connected to the <em>workshop</em>. Then
              switch your Claude/ChatGPT connector to{" "}
              <K>{MCP_URL}/weather</K> and try: "what's the weather in Boston?" —
              your data tool fires, no name-dropping needed.
            </>
          }
        />
      </DocSection>

      <PageFooter
        prev={{ to: "/docs/quickstart", label: "Quickstart" }}
        next={{ to: "/docs/widgets", label: "Building widgets with dynamic_widget" }}
      />
    </DocPage>
  );
}
